import { useRef } from "react";
import {
  Grid,
  GridNoRecords,
  GridColumn,
  type GridHandle,
  type GridProps,
  type GridColumnProps,
} from "@progress/kendo-react-grid";
import Alert from "@/components/shared/Alert";
import AccessiblePager from "@/components/shared/tables/AccessiblePager";
import { useEnterActivatableDataCell } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";

type TableProps = Pick<
  GridProps,
  | "data"
  | "onRowClick"
  | "onDataStateChange"
  | "autoProcessData"
  | "skip"
  | "take"
  | "filter"
  | "sort"
> & {
  columns: GridColumnProps[];
  error?: Error | null;
  noItemsMessage?: string;
};

//  cells={{ data: CustomCell }}

// interface TableProps extends GridProps {};
//  = {
//     data?: any[];
//     // columns: ColumnDef<TData, any>[];
//     // enableColumnFilters?: boolean;
//     // columnFilters?: ColumnFiltersState; // Optional external column filters state
//     // onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>; // Optional external column filters state
//     // loading?: boolean;
//     // error?: Error | null;
//     // noItemsMessage?: string | React.ReactNode;
//     // rowClickFn?: (row: Row<TData>) => void;
//     // initialPageSize?: number;
//     // maxHeight?: number; // Units: pixels; Note: If maxHeight is set, classes are applied to create sticky headers
//     // // initialSortingState?: SortingState | undefined;
// }

const DEFAULT_MIN_COLUMN_WIDTH = 200; // px

export default function KendoTableServerState({
  data,
  onRowClick,
  onDataStateChange,
  autoProcessData = true,
  skip,
  take,
  filter,
  sort,
  columns,
  error,
  noItemsMessage = "No items to show",
}: TableProps) {
  const gridRef = useRef<GridHandle>(null);
  const EnterActivatableDataCell = useEnterActivatableDataCell(
    onRowClick,
    gridRef
  );

  return error ? (
    <Alert variant="danger">
      <p className="mb-0">{error.message}</p>
    </Alert>
  ) : (
    <>
      {/* Kendo's grid keyboard navigation lands on the first data cell,
                not the sortable header row, with no visual/AT indication that
                arrow keys reach it. This note makes that discoverable without
                being visible to mouse users. */}
      <span tabIndex={0} className="visually-hidden-focusable">
        This table has sortable columns. Use the arrow keys to move between
        cells; press the up arrow to reach the column headers, then Enter or
        Space to sort.
      </span>
      <Grid
        ref={gridRef}
        data={data}
        onRowClick={onRowClick}
        onDataStateChange={onDataStateChange}
        autoProcessData={autoProcessData}
        skip={skip}
        take={take}
        filter={filter}
        sort={sort}
        defaultTake={10}
        pageable={true}
        pager={AccessiblePager}
        filterable={true}
        sortable={true}
        navigatable={true}
        scrollable="scrollable"
      >
        <GridNoRecords>{noItemsMessage}</GridNoRecords>
        {columns.map((column) => (
          <GridColumn
            key={column.field}
            {...column}
            cells={
              EnterActivatableDataCell
                ? {
                    ...column.cells,
                    data: column.cells?.data ?? EnterActivatableDataCell,
                  }
                : column.cells
            }
            width={column.width ?? DEFAULT_MIN_COLUMN_WIDTH}
          />
        ))}
      </Grid>
    </>
  );
}
